import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, Plus, Search, Edit, Trash2, X } from 'lucide-react';
import API_BASE_URL from '../config/api';

const CLINIC_FIELDS = [
  { key: 'name', label: 'Name *', required: true },
  { key: 'tradingName', label: 'Trading Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'fax', label: 'Fax' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website', label: 'Website' },
  { key: 'street', label: 'Street' },
  { key: 'suburb', label: 'Suburb' },
  { key: 'city', label: 'City' },
  { key: 'postCode', label: 'Post Code' },
  { key: 'country', label: 'Country' },
  { key: 'postalStreet', label: 'Postal Street' },
  { key: 'postalSuburb', label: 'Postal Suburb' },
  { key: 'postalCity', label: 'Postal City' },
  { key: 'postalPostCode', label: 'Postal Post Code' },
  { key: 'postalCountry', label: 'Postal Country' },
  { key: 'healthlinkEdi', label: 'HealthLink EDI' },
  { key: 'notes', label: 'Notes', multiline: true },
];

function buildEmptyForm() {
  const form = {};
  CLINIC_FIELDS.forEach(f => { form[f.key] = ''; });
  return form;
}

function ClinicsPage() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Debounce search query by 400ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchClinics = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/clinics`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setClinics(response.data);
    } catch (err) {
      console.error('Error fetching clinics:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  const openAddModal = () => {
    setEditingClinic(null);
    setForm(buildEmptyForm());
    setError('');
    setShowModal(true);
  };

  const openEditModal = (clinic) => {
    setEditingClinic(clinic);
    const f = {};
    CLINIC_FIELDS.forEach(field => {
      f[field.key] = clinic[field.key] || '';
    });
    setError('');
    setForm(f);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClinic(null);
    setForm(buildEmptyForm());
    setError('');
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingClinic) {
        await axios.put(`${API_BASE_URL}/clinics/${editingClinic.id}`, form, { headers });
      } else {
        await axios.post(`${API_BASE_URL}/clinics`, form, { headers });
      }
      closeModal();
      fetchClinics();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save clinic');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clinic) => {
    if (!window.confirm(`Are you sure you want to delete ${clinic.name}?`)) return;
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/clinics/${clinic.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchClinics();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete clinic');
    }
  };

  if (loading && clinics.length === 0) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{
          width: '48px', height: '48px',
          border: '4px solid #e5e7eb', borderTop: '4px solid #2563eb',
          borderRadius: '50%', animation: 'spin 1s linear infinite',
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 style={{ width: '28px', height: '28px', color: '#2563eb' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>Clinics</h1>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Controls Row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 300px', minWidth: '200px' }}>
            <Search style={{
              position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
              width: '18px', height: '18px', color: '#9ca3af',
            }} />
            <input
              type="text"
              placeholder="Search by name, trading name, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 40px',
                border: '2px solid #d1d5db', borderRadius: '8px',
                fontSize: '14px', boxSizing: 'border-box', outline: 'none',
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Add Clinic */}
          <button
            onClick={openAddModal}
            style={{
              display: 'flex', alignItems: 'center', padding: '10px 20px',
              backgroundColor: '#2563eb', color: 'white', border: 'none',
              borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
              fontWeight: '500', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          >
            <Plus style={{ width: '18px', height: '18px', marginRight: '6px' }} />
            Add Clinic
          </button>
        </div>

        {/* Table Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#111827' }}>
              All Clinics ({clinics.length})
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  {['Name', 'Trading Name', 'City', 'Phone', 'Email', 'Fax', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '12px 24px', textAlign: 'left', fontSize: '12px',
                      fontWeight: '500', color: '#6b7280', textTransform: 'uppercase',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clinics.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                      {searchQuery ? 'No clinics match your search.' : 'No clinics yet. Add one or run the import script.'}
                    </td>
                  </tr>
                ) : (
                  clinics.map((clinic, index) => (
                    <tr key={clinic.id} style={{ borderTop: index > 0 ? '1px solid #e5e7eb' : 'none' }}>
                      <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {clinic.name}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {clinic.tradingName || '-'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {clinic.city || '-'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {clinic.phone || '-'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {clinic.email || '-'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {clinic.fax || '-'}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => openEditModal(clinic)}
                            style={{
                              display: 'flex', alignItems: 'center', padding: '6px 12px',
                              backgroundColor: '#dbeafe', color: '#1e40af', border: 'none',
                              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                          >
                            <Edit style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(clinic)}
                            style={{
                              display: 'flex', alignItems: 'center', padding: '6px 12px',
                              backgroundColor: '#fee2e2', color: '#dc2626', border: 'none',
                              borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                          >
                            <Trash2 style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '32px',
            maxWidth: '640px', width: '90%', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#111827' }}>
                {editingClinic ? 'Edit Clinic' : 'Add Clinic'}
              </h2>
              <button
                onClick={closeModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {error && (
              <div style={{
                backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '8px', padding: '12px', marginBottom: '16px',
              }}>
                <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSave}>
              {CLINIC_FIELDS.map(field => (
                <div key={field.key} style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    {field.label}
                  </label>
                  {field.multiline ? (
                    <textarea
                      value={form[field.key]}
                      onChange={(e) => handleFormChange(field.key, e.target.value)}
                      rows={3}
                      style={{
                        width: '100%', padding: '8px 12px',
                        border: '2px solid #d1d5db', borderRadius: '8px',
                        fontSize: '14px', boxSizing: 'border-box',
                        resize: 'vertical', fontFamily: 'inherit',
                      }}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      value={form[field.key]}
                      onChange={(e) => handleFormChange(field.key, e.target.value)}
                      style={{
                        width: '100%', padding: '8px 12px',
                        border: '2px solid #d1d5db', borderRadius: '8px',
                        fontSize: '14px', boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
              ))}

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1, padding: '10px 20px',
                    backgroundColor: saving ? '#93c5fd' : '#2563eb', color: 'white',
                    border: 'none', borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '500',
                  }}
                  onMouseEnter={(e) => { if (!saving) e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
                  onMouseLeave={(e) => { if (!saving) e.currentTarget.style.backgroundColor = '#2563eb'; }}
                >
                  {saving ? 'Saving...' : (editingClinic ? 'Save Changes' : 'Add Clinic')}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    flex: 1, padding: '10px 20px',
                    backgroundColor: '#e5e7eb', color: '#374151',
                    border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClinicsPage;
